import React, { useState, useEffect } from 'react';
import useLanguageDetector from './components/useLanguageDetector';
import JapanMap from './components/JapanMap';
import { seoTranslations } from './components/seo/seoConfig';
import { chinaSeoTranslations } from './components/seo/chinaSeoConfig';
import {BrowserRouter as Router, Route, Routes, useLocation} from 'react-router-dom';
import ChinaMap from "./components/map/ChinaMap";

const SEOUpdater = ({ currentLang, isChina }) => {
  const seo = isChina ? chinaSeoTranslations[currentLang] : seoTranslations[currentLang];
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = currentLang;
    document.title = seo.title;

    const updateMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateOgTag = (property, content) => {
      let meta = document.querySelector(`meta[property="og:${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', `og:${property}`);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', seo.description);
    updateMetaTag('keywords', seo.keywords);
    updateMetaTag('language', currentLang);
    updateMetaTag('author', isChina ? 'ChinaEx' : 'JapanEx');
    updateMetaTag('robots', 'index, follow');

    updateOgTag('title', seo.ogTitle);
    updateOgTag('description', seo.ogDescription);
    updateOgTag('type', 'website');
    updateOgTag('site_name', isChina ? 'ChinaEx' : 'JapanEx');

    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }

    const basePath = (isChina ? '/china' : '') + '/' + currentLang;
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.href = `https://world-ex.org${basePath}`;
    }

    const jsonLdContent = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": seo.title,
      "description": seo.description,
      "applicationCategory": "Game",
      "operatingSystem": "Web Browser",
      "inLanguage": currentLang,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    };

    jsonLd.textContent = JSON.stringify(jsonLdContent);
  }, [currentLang, seo, location.pathname, isChina]);

  return null;
};

const Footer = () => {
  const location = useLocation();
  const [currentLang] = useState('en');

  const seo = location.pathname.includes('/china') ?
    chinaSeoTranslations[currentLang] :
    seoTranslations[currentLang];

  return (
    <footer className="bg-white py-8 mt-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-sm text-gray-600">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {seo.footerTitle}
          </h2>
          <p className="mb-4">{seo.footerDescription}</p>
          <p>{seo.footerExtra}</p>
        </div>
      </div>
    </footer>
  );
};

const App = () => {
  const detectedLanguage = useLanguageDetector();
  const [currentLang, setCurrentLang] = useState('en');

  useEffect(() => {
    if (detectedLanguage) {
      setCurrentLang(detectedLanguage);
    }
  }, [detectedLanguage]);

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <>
            <SEOUpdater currentLang={currentLang} isChina={false} />
            <JapanMap currentLanguage={currentLang} />
            <Footer />
          </>
        } />
        {['en', 'ja', 'zh', 'ko', 'es', 'fr', 'de', 'it', 'pt', 'ru'].map(lang => (
          <Route key={lang} path={`/${lang}`} element={
            <>
              <SEOUpdater currentLang={lang} isChina={false} />
              <JapanMap currentLanguage={lang} />
              <Footer />
            </>
          } />
        ))}

        <Route path="/china" element={
          <>
            <SEOUpdater currentLang={currentLang} isChina={true} />
            <ChinaMap currentLanguage={currentLang} />
            <Footer />
          </>
        } />
        {['en', 'ja', 'zh', 'ko', 'es', 'fr', 'de', 'it', 'pt', 'ru'].map(lang => (
          <Route key={`china-${lang}`} path={`/china/${lang}`} element={
            <>
              <SEOUpdater currentLang={lang} isChina={true} />
              <ChinaMap currentLanguage={lang} />
              <Footer />
            </>
          } />
        ))}
      </Routes>
    </Router>
  );
};

export default App;